import { useRouter } from 'next/router';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useDialog, useHandleApiCall, useNotification } from '../hooks';
import {
  API_CONSTANTS,
  Audio,
  AvatarDataType,
  axiosInstance,
  CreateVideoContextType,
  CreateVideoSideMenuItemEnum,
  InitialVideoData,
  isCreditsAvailable,
  RequiredCreditsResponse,
  TalkingFaceModelType,
  validateCreateVideoInputs,
  VideoRatioEnum,
} from '../utils';
import { useSnackbar } from 'notistack';
import { CreditDialog } from '../components/create-video/CreditDialog';
import { StockBrollItem } from '../services/broll-caption/fetchBrollCaptionData';
import { AppContext } from './AppContext';
import { getUploadId, putPreSignedUrl, uploadFileToPresignedUrl } from '../services';

interface CreditDetail {
  availableCredit: number;
  creditNeeded: number;
  creditsInUse?: number;
}

interface DraftVideoParams {
  disableNavigation?: boolean;
  imageId?: string;
}

interface CreateVideoProviderProps {
  children: React.ReactNode;
  disabled?: boolean;
}

export const CreateVideoContext = createContext<CreateVideoContextType | null>(null);

const isValidEnumValue = (value: any): value is CreateVideoSideMenuItemEnum => {
  return Object.values(CreateVideoSideMenuItemEnum).includes(value);
};

interface CreateVideoProviderProps {
  children: React.ReactNode;
  disabled?: boolean;
}

export const CreateVideoProvider = ({ children, disabled = false }: CreateVideoProviderProps) => {
  const { push, query } = useRouter();
  const router = useRouter();
  const { post } = useHandleApiCall();
  const { showNotification } = useNotification();
  const { draftVideoId, audioid, tab } = query;
  const [currentStep, setCurrentStep] = useState<'first' | 'second'>('first');
  const [avatarBackground, setAvatarBackground] = useState('');
  const colors = ['#FF5733', '#33FF57', '#3357FF', '#F3FF33', '#FF33A6'];
  const [bgImages, setBgImages] = useState<StockBrollItem[]>([]);
  const [bgColor, setBgColor] = useState('');
  const [searchQuery, setSearchQuery] = useState<string>('background');
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const [selectedAvatar, setSelectedAvatar] = useState<AvatarDataType | null>(null);
  const [videoText, setVideoText] = useState<string>('');
  const [videoEffectPrompt, setVideoEffectPrompt] = useState<string>(''); 
  const [selectedAudio, setSelectedAudio] = useState<Audio | null>(null);
  const [videoRatio, setVideoRatio] = useState<VideoRatioEnum>(VideoRatioEnum.landscape);
  const [draftAudioId, setDraftAudioId] = useState<string>('');
  const [selectedSideMenu, setSelectedSideMenu] = useState<CreateVideoSideMenuItemEnum>(
    isValidEnumValue(tab) ? tab : CreateVideoSideMenuItemEnum.SCRIPT
  );
  const [videoName, setVideoName] = useState<string>('');
  const [modelName, setModelName] = useState<TalkingFaceModelType>(TalkingFaceModelType.HEDRA);
  const [isCreditDialogOpen, setIsCreditDialogOpen] = useState(false);
  const [creditDetail, setCreditDetail] = useState<CreditDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [initialVideoData, setInitialVideoData] = useState<InitialVideoData>({});
  const [isTrain, setIsTrain] = useState(false);
  const [enableMotion, setEnableMotion] = useState(true);

  const { enqueueSnackbar } = useSnackbar();
  const { showCreditDialog } = useContext(AppContext);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // mutations
  const createVideoMutation = post(
    API_CONSTANTS.CREATE_VIDEOS.key,
    API_CONSTANTS.CREATE_VIDEOS.path
  );
  const getRemainingCredits = post(
    API_CONSTANTS.REQUIRED_CREDITS.key,
    API_CONSTANTS.REQUIRED_CREDITS.path
  );

  const {
    closeDialog: closePreviewModal,
    isDialogOpen: isOpenPreviewModal,
    openDialog: openPreviewModal,
  } = useDialog();

  const navigateToVideoPage = useCallback(() => {
    push('/your-videos');
  }, [push]);

  const navigateToDraftPage = useCallback(() => {
    // push('/content-draft');
    push('/your-videos');
  }, [push]);

  const handleCloseCreditDialog = () => {
    setIsCreditDialogOpen(false);
  };

  const removeBg = () => {
    setBgColor('');
    setAvatarBackground('');
    setSelectedImageIndex(null);
  }

  useEffect(() => {
    if (modelName === TalkingFaceModelType.HEYGEN) {
      removeBg();
    }
  }, [modelName])

  useEffect( () => {
    if (modelName === TalkingFaceModelType.HEYGEN && selectedAvatar) {
      if (selectedAvatar?.metaData?.heyGenMotionId || !enableMotion) {
        setIsTrain(false);
      } else {
        setIsTrain(true);
      }
    } else {
      setIsTrain(false);
    }
  }, [modelName, selectedAvatar, enableMotion]);

  const handleFileUpload = async () => {
    try {
      const canvas = canvasRef.current;
      if (!canvas) return;

      try {
        // Convert canvas to Blob
        const blob = await new Promise<Blob | null>((resolve, reject) => {
          try {
            canvas.toBlob(
              (result) => {
                if (result) {
                  resolve(result);
                } else {
                  reject(new Error('Failed to create blob from canvas'));
                }
              }, 
              'image/jpeg', 
              1
            );
          } catch (blobError) {
            reject(blobError);
          }
        });

        if (!blob) {
          throw new Error('Could not create blob from canvas');
        }

        // Convert Blob to File
        const files = new File([blob], 'final-image.jpg', { type: 'image/jpeg' });
        const { uploadId, preSignedUrls, format } = await putPreSignedUrl(files);
        await uploadFileToPresignedUrl(uploadId, preSignedUrls, files);
        let mimeType = 'image/jpeg';
        const uploadDetails = await getUploadId(preSignedUrls[0].url, format, mimeType);
        return uploadDetails.id;
      } catch (canvasError) {
        console.error('Error with canvas conversion:', canvasError);
        throw new Error('Failed to export canvas image: ' + canvasError.message);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  };

  const handleConfirmCreditAction = async () => {
    setIsLoading(true);
    try {
      let imageId;
      try {
        imageId = await handleFileUpload();
      } catch (uploadError) {
        // Check for both SecurityError and tainted canvas messages
        if (
          (uploadError instanceof DOMException && uploadError.name === 'SecurityError') || 
          (uploadError.message && (
            uploadError.message.includes('Tainted canvases may not be exported') || 
            uploadError.message.includes('SecurityError') || 
            uploadError.message.includes('canvas has been tainted')
          ))
        ) {
          showNotification(
            'Security restrictions prevent exporting the canvas. This usually happens when images from different origins are used. ' +
            'Please try a different avatar or background image.', 
            'error'
          );
        } else {
          showNotification('Failed to process canvas image: ' + uploadError.message, 'error');
        }
        console.error('Canvas export error:', uploadError);
        setIsLoading(false);
        setIsRenderInProgress(false);
        handleCloseCreditDialog();
        return;
      }
      
      if (draftVideoId) {
        await handleDraftVideo({imageId});
        await axiosInstance.post('api/videos/remove-draft', {
          creationId: draftVideoId,
        });
        showNotification('Video Creation is in-Progress', 'success');
      } else {
        await createVideoMutation.mutateAsync({
          avatarId: selectedAvatar.id,
          text: videoText,
          videoEffectPrompt,
          modelName,
          enableMotion,
          audioId: selectedAudio.id,
          name: videoName,
          background: avatarBackground,
          processedBackgroundId: imageId,
        });
      }

      showNotification('Video Creation is in-Progress', 'success');
      navigateToVideoPage();
      handleFinalClose();
    } catch (err) {
      console.error(err);
      showNotification('Video creation failed: ' + (err.message || 'Unknown error'), 'error');
    }
    handleCloseCreditDialog();
    setIsLoading(false);
    // Reset the rendering state
    setIsRenderInProgress(false);
  };

  const handleUpgradeClick = async () => {
    setIsLoading(true);
    const newDraftId = await handleDraftVideo({ disableNavigation: true });
    push(`/subscription?videoId=${draftVideoId ?? newDraftId}`);
    handleCloseCreditDialog();
    setIsLoading(false);
  };

  const handleMenuSelect = (value: CreateVideoSideMenuItemEnum) => {
    setSelectedSideMenu(value);
  };
  const handleSelectAvatar = (avatar: AvatarDataType | null, open: boolean = true) => {
    setSelectedAvatar(avatar);
    if(avatar && open) openPreviewModal();
  };

  const handleVideoText = (text: string) => {
    setVideoText(text);
  };

  const handleVideoEffectPrompt = useCallback((text: string) => {
    // Ensure text is never undefined and always a string
    setVideoEffectPrompt(text || '');
  }, []);

  const handleSelectAudio = (audio: Audio | null) => {
    setSelectedAudio(audio);
    setDraftAudioId(null);
  };

  const changeStep = () => {
    if (audioid) {
      push(`/create-video-details?audioid=${audioid}`);
    } else {
      push('/create-video-details');
    }
    handleCloseModel();
  };

  const handleCloseModel = () => {
    const { avatarid, ...rest } = router.query;
    router.replace(
      {
        pathname: router.pathname,
        query: rest,
      },
      undefined,
      { shallow: true }
    );
    closePreviewModal();
  }

  const changeVideoRatio = (value: VideoRatioEnum) => {
    setVideoRatio(value);
  };

  const handleVideoName = useCallback((name: string) => {
    setVideoName(name);
  }, []);

  const handleFinalClose = () => {
    setVideoText('');
    setVideoEffectPrompt('');
    setSelectedAudio(null);
    setSelectedAvatar(null);
    // navigation call goes here
    setCurrentStep('first');
    // Reset rendering state
    setIsRenderInProgress(false);
  };

  const getContent = async () => {
    const getVideoResponse = await axiosInstance.post('api/videos/get-video', {
      creationId: draftVideoId,
    });
    const getVideoContent = await axiosInstance.post('api/speech-content/get-content', {
      speechContentId: getVideoResponse.data.speechContentId,
    });
    const { userAudioTrackId, name, audioTrack, background, avatar, modelName, enableMotion } = getVideoResponse.data;
    setAvatarBackground(background || '');
    setDraftAudioId(userAudioTrackId);
    handleVideoName(name);
    handleSelectAvatar(getVideoResponse.data.avatar,false);
    handleVideoText(getVideoContent.data);
    // Ensure videoEffectPrompt is never undefined by providing default empty string
    handleVideoEffectPrompt(getVideoResponse.data.videoEffectPrompt || '');
    setSelectedAudio(audioTrack);
    setModelName(modelName === 'heyGen' ? TalkingFaceModelType.HEYGEN : TalkingFaceModelType.HEDRA);
    setEnableMotion(enableMotion);
    setInitialVideoData({
      avatarBackground: background || '',
      videoName: name,
      selectedAvatarId: avatar?.id,
      videoText: getVideoContent.data,
      selectedAudioId: audioTrack?.id,
      modelName: modelName === 'heyGen' ? TalkingFaceModelType.HEYGEN : TalkingFaceModelType.HEDRA,
      enableMotion: enableMotion,
    });
    if(modelName === 'heyGen') {
      removeBg();
    }
  };

  useEffect(() => {
    if (draftVideoId) {
      getContent();
    }
  }, [draftVideoId]);

  const handleDraftVideo = useCallback(
    async ({ disableNavigation = false, imageId = '' }: DraftVideoParams = {}): Promise<void | string> => {
      const isValid = validateCreateVideoInputs({
        videoText,
        videoEffectPrompt,
        selectedAvatar: selectedAvatar?.id,
        selectedAudio: selectedAudio?.id,
        videoName,
        enqueueSnackbar,
      });

      if (!isValid) {
        return;
      }

      try {
        if (draftVideoId) {
          const getVideoResponse = await axiosInstance.post('api/videos/get-video', {
            creationId: draftVideoId,
          });
          const getVideoContent = await axiosInstance.post('api/speech-content/get-content', {
            speechContentId: getVideoResponse.data.speechContentId,
          });
          if (
            selectedAvatar.id !== getVideoResponse.data.avatar_id ||
            selectedAudio.id !== getVideoResponse.data.userAudioTrackId ||
            videoText !== getVideoContent.data ||
            videoEffectPrompt !== getVideoResponse.data.videoEffectPrompt ||
            imageId
          ) {
            const processedBackgroundId = imageId || '';
            await axiosInstance.post('api/videos/save-draft-content', {
              videoName,
              avatarId: selectedAvatar.id,
              audioId: selectedAudio.id,
              background: avatarBackground,
              processedBackgroundId,
              text: videoText,
              creationId: draftVideoId,
              videoEffectPrompt,
              modelName,
              enableMotion,
            });
            setInitialVideoData({
              avatarBackground,
              videoName,
              selectedAvatarId: selectedAvatar?.id,
              videoText,
              videoEffectPrompt,
              selectedAudioId: selectedAudio?.id,
              modelName,
              enableMotion,
            });
          } else {
            showNotification('Already saved draft', 'success');
            if (!disableNavigation) navigateToDraftPage();
            return;
          }
        } else {
          const data = await axiosInstance.post('api/videos/save-draft', {
            name: videoName,
            avatarId: selectedAvatar.id,
            audioId: selectedAudio.id,
            background: avatarBackground,
            content: videoText,
            modelName,
            enableMotion,
          });
          setInitialVideoData({
            avatarBackground,
            videoName,
            selectedAvatarId: selectedAvatar?.id,
            videoText,
            selectedAudioId: selectedAudio?.id,
          });
          if (disableNavigation && data.data && data.data.id) return data.data.id;
        }
      } catch (err) {
        console.error(err);
        showNotification('Save draft failed', 'error');
      }
      if (!disableNavigation) navigateToDraftPage();
      handleFinalClose();
    },
    [selectedAudio?.id, selectedAvatar?.id, videoText, videoEffectPrompt, modelName, enableMotion, videoName, avatarBackground, draftVideoId]
  );

  const [isFetchingCreditDetail, setIsFetchingCreditDetail] = useState(false);
  // Flag to track if render is in progress
  const [isRenderInProgress, setIsRenderInProgress] = useState(false);
  
  const handleRenderVideo = useCallback(async () => {
    // Prevent multiple submissions
    if (isRenderInProgress) {
      return;
    }
    
    const isValid = validateCreateVideoInputs({
      videoText,
      videoEffectPrompt,
      selectedAvatar: selectedAvatar?.id,
      selectedAudio: selectedAudio?.id,
      videoName,
      enqueueSnackbar,
    });

    if (!isValid) {
      return;
    }
    
    setIsRenderInProgress(true);
    setIsFetchingCreditDetail(true);
    
    try {
      const { availableCredits, creditsInUse, requiredCredits }: RequiredCreditsResponse =
        await getRemainingCredits.mutateAsync({
          text: videoText,
          model: modelName,
          isTrain,
        });
      setCreditDetail({
        availableCredit: availableCredits <= 0 ? 0 : availableCredits,
        creditNeeded: requiredCredits,
        creditsInUse: creditsInUse,
      });
    } catch (err) {
      console.error(err);
      showNotification('Video creation failed', 'error');
    } finally {
      setIsFetchingCreditDetail(false);
      // Reset the flag after a short delay to prevent multiple submissions
      setTimeout(() => {
        setIsRenderInProgress(false);
      }, 1000);
    }
  }, [
    selectedAudio?.id, 
    selectedAvatar?.id, 
    videoText, 
    videoEffectPrompt, 
    modelName,
    enableMotion,
    videoName, 
    enqueueSnackbar, 
    getRemainingCredits, 
    showNotification, 
    isRenderInProgress
  ]);

  useEffect(() => {
    if (creditDetail && !isFetchingCreditDetail) {
      const isEnoughCredit = isCreditsAvailable(
        creditDetail?.availableCredit,
        creditDetail?.creditsInUse,
        creditDetail?.creditNeeded
      );
      
      // Set the credit dialog state once
      if (showCreditDialog) {
        setIsCreditDialogOpen(true);
      } else {
        if (isEnoughCredit) {
          // Use setTimeout to prevent multiple calls in rapid succession
          setTimeout(() => {
            handleConfirmCreditAction();
          }, 100);
        } else if (!isEnoughCredit) {
          // Use setTimeout to prevent multiple calls in rapid succession
          setTimeout(() => {
            handleUpgradeClick();
          }, 100);
        }
      }
      
      // Clear creditDetail to prevent this effect from running again with the same data
      // setTimeout(() => {
      //   setCreditDetail(null);
      // }, 500);
    }
  }, [creditDetail, isFetchingCreditDetail]);

  // Create a default context value for disabled state
  const contextValue = disabled ? {
    // Provide non-functional stubs when disabled
    canvasRef: null,
    selectedAvatar: null,
    videoText: '',
    videoEffectPrompt: '',
    selectedAudio: null,
    videoRatio,
    videoName: '',
    draftAudioId: '',
    setAvatarBackground: (_bg: string) => { /* no-op */ },
    avatarBackground: '',
    colors,
    bgColor: '',
    setBgColor: (_color: string) => { /* no-op */ },
    bgImages: [],
    setBgImages: (_images: StockBrollItem[]) => { /* no-op */ },
    searchQuery: '',
    setSearchQuery: (_query: string) => { /* no-op */ },
    selectedImageIndex: null,
    setSelectedImageIndex: (_index: number | null) => { /* no-op */ },
    handleSelectAvatar: (_avatar: AvatarDataType | null, _open?: boolean) => { /* no-op */ },
    handleVideoText: (_text: string) => { /* no-op */ },
    handleVideoEffectPrompt: (_text: string) => { /* no-op */ },
    isRenderInProgress: false,
    handleSelectAudio: (_audio: Audio | null) => { /* no-op */ },
    changeStep: () => { /* no-op */ },
    currentStep,
    changeVideoRatio: (_ratio: VideoRatioEnum) => { /* no-op */ },
    selectedSideMenu,
    handleMenuSelect: (_menu: CreateVideoSideMenuItemEnum) => { /* no-op */ },
    handleRenderVideo: async () => { /* no-op */ },
    handleVideoName: (_name: string) => { /* no-op */ },
    handleDraftVideo: async () => { /* no-op */ },
    initialVideoData: {},
    closePreviewModal: () => { /* no-op */ },
    handleCloseModel: () => { /* no-op */ },
    isOpenPreviewModal: false,
    modelName,
    setModelName,
    enableMotion,
    setEnableMotion,
    isTrain
  } : {
    // Normal functionality when enabled
    canvasRef,
    selectedAvatar,
    videoText,
    videoEffectPrompt,
    selectedAudio,
    videoRatio,
    videoName,
    draftAudioId,
    setAvatarBackground,
    avatarBackground,
    colors,
    bgColor,
    setBgColor,
    bgImages,
    setBgImages,
    searchQuery,
    setSearchQuery,
    selectedImageIndex,
    setSelectedImageIndex,
    handleSelectAvatar,
    handleVideoText,
    handleVideoEffectPrompt,
    isRenderInProgress,
    handleSelectAudio,
    changeStep,
    currentStep,
    changeVideoRatio,
    selectedSideMenu,
    handleMenuSelect,
    handleRenderVideo,
    handleVideoName,
    handleDraftVideo,
    initialVideoData,
    closePreviewModal,
    handleCloseModel,
    isOpenPreviewModal,
    modelName,
    setModelName,
    enableMotion,
    setEnableMotion,
    isTrain,
    ...(typeof draftVideoId === 'string' && { draftVideoId }),
  };

  return (
    <CreateVideoContext.Provider value={contextValue}>
      {children}

      <CreditDialog
        isOpen={isCreditDialogOpen}
        creditDetail={creditDetail}
        onClose={handleCloseCreditDialog}
        onConfirm={handleConfirmCreditAction}
        onUpgrade={handleUpgradeClick}
        isFetchingCreditDetail={isFetchingCreditDetail}
        isLoading={isLoading}
        isTrain={isTrain}
      />
    </CreateVideoContext.Provider>
  );
};
